import React, { useState, useEffect } from "react";
import { Plus, X } from "lucide-react";
import customFieldsService from "../services/customFieldsService";
import { CustomFieldDefinition } from "../types/customFields";
import { renderInput } from "./CustomFieldInput";
import InlineFieldCreator from "./InlineFieldCreator";

interface FieldAssignment {
  definitionId: string;
  value: string | number | boolean | null;
}

export interface AddSubscribersPanelProps {
  handleAddSubscriber: (
    email: string,
    customFields?: Record<string, string | number | boolean | null>,
    bypassValidation?: boolean,
    skipWelcomeEmail?: boolean
  ) => Promise<{ success: boolean; error?: string }>;
}

const AddSubscribersPanel: React.FC<AddSubscribersPanelProps> = ({ handleAddSubscriber }) => {
  const [subscriberEmail, setSubscriberEmail] = useState("");
  const [bypassValidation, setBypassValidation] = useState(false);
  const [skipWelcomeEmail, setSkipWelcomeEmail] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [definitions, setDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [fieldAssignments, setFieldAssignments] = useState<FieldAssignment[]>([]);
  const [showAddField, setShowAddField] = useState(false);
  const [showFields, setShowFields] = useState(false);

  useEffect(() => {
    customFieldsService.listDefinitions().then(setDefinitions).catch(console.error);
  }, []);

  const onAdd = async () => {
    setErrorMessage("");
    const customFields: Record<string, string | number | boolean | null> = {};
    for (const a of fieldAssignments) {
      if (a.definitionId && a.value !== null) {
        customFields[a.definitionId] = a.value;
      }
    }
    const hasFields = Object.keys(customFields).length > 0;
    const result = await handleAddSubscriber(
      subscriberEmail,
      hasFields ? customFields : undefined,
      bypassValidation,
      skipWelcomeEmail
    );
    if (result.success) {
      setSubscriberEmail("");
      setFieldAssignments([]);
      setShowFields(false);
      setBypassValidation(false);
      setSkipWelcomeEmail(false);
    } else if (result.error) {
      // Persist until dismissed or the next attempt — validation failures
      // include instructions the user needs time to read
      setErrorMessage(result.error);
    }
  };

  const addAssignment = () => {
    setFieldAssignments([...fieldAssignments, { definitionId: "", value: null }]);
  };

  const removeAssignment = (index: number) => {
    setFieldAssignments(fieldAssignments.filter((_, i) => i !== index));
  };

  const updateAssignment = (index: number, update: Partial<FieldAssignment>) => {
    setFieldAssignments(fieldAssignments.map((a, i) => {
      if (i !== index) return a;
      const updated = { ...a, ...update };
      // Reset value when changing field definition
      if (update.definitionId !== undefined && update.definitionId !== a.definitionId) {
        updated.value = null;
      }
      return updated;
    }));
  };

  const usedDefinitionIds = new Set(fieldAssignments.map((a) => a.definitionId).filter(Boolean));
  const availableDefinitions = definitions.filter((d) => !usedDefinitionIds.has(String(d.id)));

  const handleFieldCreated = (def: CustomFieldDefinition) => {
    setDefinitions((prev) => [...prev, def]);
    setShowAddField(false);
    // Auto-add an assignment row for the newly created field
    setFieldAssignments((prev) => [...prev, { definitionId: String(def.id), value: null }]);
  };

  return (
    <div>
      <h1>Add Subscriber</h1>

      <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
        <label style={{ fontWeight: "600", fontSize: "16px" }}>Email:</label>
        <input
          type="email"
          value={subscriberEmail}
          onChange={(e) => setSubscriberEmail(e.target.value)}
          className="url-input"
          style={{ width: "100%" }}
        />

        {/* Custom Fields Section */}
        <div>
          <button
            onClick={() => {
              setShowFields(!showFields);
              if (!showFields && fieldAssignments.length === 0) {
                addAssignment();
              }
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#6b7280",
              fontSize: "14px",
              padding: "0",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Plus size={14} />
            {showFields ? "Hide custom fields" : "Add custom field"}
          </button>

          {showFields && (
            <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
              {fieldAssignments.map((assignment, index) => {
                const selectedDef = definitions.find((d) => String(d.id) === assignment.definitionId);
                return (
                  <div key={index} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                    <select
                      value={assignment.definitionId}
                      onChange={(e) => updateAssignment(index, { definitionId: e.target.value })}
                      style={{
                        flex: 1,
                        padding: "6px 10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    >
                      <option value="">Select field...</option>
                      {definitions
                        .filter((d) => String(d.id) === assignment.definitionId || !usedDefinitionIds.has(String(d.id)))
                        .map((d) => (
                          <option key={d.id} value={String(d.id)}>
                            {d.name} ({d.field_type})
                          </option>
                        ))}
                    </select>
                    {selectedDef && (
                      <div style={{ flex: 1 }}>
                        {renderInput(selectedDef, assignment.value, (val) =>
                          updateAssignment(index, { value: val })
                        )}
                      </div>
                    )}
                    <button
                      onClick={() => removeAssignment(index)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#9ca3af",
                        padding: "6px",
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                );
              })}

              <div style={{ display: "flex", gap: "12px" }}>
                {availableDefinitions.length > 0 && (
                  <button
                    onClick={addAssignment}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#6b7280",
                      fontSize: "13px",
                      padding: "0",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Plus size={12} /> Add another field
                  </button>
                )}
                <button
                  onClick={() => setShowAddField(true)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#6b7280",
                    fontSize: "13px",
                    padding: "0",
                  }}
                >
                  + Create new field
                </button>
              </div>

              {showAddField && (
                <InlineFieldCreator
                  onFieldCreated={handleFieldCreated}
                  onCancel={() => setShowAddField(false)}
                />
              )}
            </div>
          )}
        </div>

        <label
          title="Emails are normally checked with a validation service before being added. Skipping this check can hurt deliverability if the address is invalid or likely to mark emails as spam."
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "14px",
            color: "#6b7280",
            cursor: "pointer",
            width: "fit-content",
          }}
        >
          <input
            type="checkbox"
            checked={bypassValidation}
            onChange={(e) => setBypassValidation(e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          Bypass validation ⓘ
        </label>

        <label
          title="This subscriber will not receive the welcome journey or welcome email configured for this list."
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "14px",
            color: "#6b7280",
            cursor: "pointer",
            width: "fit-content",
          }}
        >
          <input
            type="checkbox"
            checked={skipWelcomeEmail}
            onChange={(e) => setSkipWelcomeEmail(e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          Skip welcome email ⓘ
        </label>

        {errorMessage && (
          <div style={{
            color: "#b02a37",
            fontSize: "14px",
            padding: "10px 12px",
            backgroundColor: "#fdecea",
            border: "1px solid #f5c2c7",
            borderRadius: "6px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "8px",
          }}>
            <span>{errorMessage}</span>
            <button
              onClick={() => setErrorMessage("")}
              aria-label="Dismiss"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#b02a37",
                padding: "0",
                flexShrink: 0,
              }}
            >
              <X size={16} />
            </button>
          </div>
        )}
        <button
          className="primary-button"
          onClick={onAdd}
          style={{ maxWidth: "108px" }}
        >
          Add
        </button>
      </div>
    </div>
  );
};

export default AddSubscribersPanel;
